const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

export interface CloudinaryUploadResult {
  public_id: string;
  url: string;
  secure_url: string;
  width: number;
  height: number;
  format: string;
  resource_type: string;
  thumbnail_url?: string;
}

class CloudinaryService {
  /**
   * Upload image to Cloudinary
   */
  async uploadImage(file: File, folder: string = 'lagranjavota'): Promise<CloudinaryUploadResult> {
    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
      throw new Error('Cloudinary is not configured. Please set environment variables.');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folder);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();

      return {
        public_id: data.public_id,
        url: data.url,
        secure_url: data.secure_url,
        width: data.width,
        height: data.height,
        format: data.format,
        resource_type: data.resource_type,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(
    files: File[],
    folder: string = 'lagranjavota'
  ): Promise<CloudinaryUploadResult[]> {
    const uploadPromises = files.map((file) => this.uploadImage(file, folder));
    return Promise.all(uploadPromises);
  }

  /**
   * Get optimized image URL with transformations
   */
  getOptimizedUrl(
    publicId: string,
    options: {
      width?: number;
      height?: number;
      crop?: 'fill' | 'fit' | 'scale' | 'crop';
      quality?: 'auto' | number;
      format?: 'auto' | 'jpg' | 'png' | 'webp';
    } = {}
  ): string {
    if (!CLOUDINARY_CLOUD_NAME) {
      throw new Error('Cloudinary is not configured');
    }

    const {
      width,
      height,
      crop = 'fill',
      quality = 'auto',
      format = 'auto',
    } = options;

    let transformation = 'f_auto,q_auto';

    if (width) transformation += `,w_${width}`;
    if (height) transformation += `,h_${height}`;
    if (crop) transformation += `,c_${crop}`;
    if (quality !== 'auto') transformation += `,q_${quality}`;
    if (format !== 'auto') transformation += `,f_${format}`;

    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${transformation}/${publicId}`;
  }

  /**
   * Delete image from Cloudinary (requires backend implementation for security)
   */
  async deleteImage(publicId: string): Promise<void> {
    // This should be called from backend to keep API secret secure
    throw new Error('Delete image must be implemented on backend');
  }
}

export const cloudinaryService = new CloudinaryService();
